import React, { useState, useRef, useEffect } from "react";
import { Button } from "./Button";

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allOptionLabel?: string;
  className?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  allOptionLabel = "All",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter out null/undefined values, then filter based on search term
  const filteredOptions = (options ?? [])
    .filter((option): option is string => option != null)
    .filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()));

  // Update dropdown position when opened or on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const displayValue = value === "all" ? allOptionLabel : value || placeholder;

  return (
    <div className={`relative ${className}`}>
      <Button
        ref={buttonRef}
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-between space-x-2 h-10 min-w-[120px] pr-8 relative"
      >
        <span className="text-sm truncate">{displayValue}</span>
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute right-2 h-4 w-4 text-primary-blue"
          viewBox="0 0 24 24"
          fill="currentColor"
          focusable="false"
        >
          <path d="M7 10l5-5 5 5H7z" />
          <path d="M7 14l5 5 5-5H7z" />
        </svg>
      </Button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed w-72 bg-white shadow-2xl border border-border-gray z-[100] max-h-96 overflow-hidden flex flex-col"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border-gray bg-gray-50">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border border-border-gray focus:outline-none focus:ring-2 focus:ring-primary-blue"
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto">
            {/* All option */}
            <div className="p-2 border-b border-border-gray bg-gray-50">
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  value === "all" ? "bg-primary-blue text-white" : "hover:bg-gray-100"
                }`}
                onClick={() => handleSelect("all")}
              >
                {allOptionLabel}
              </button>
            </div>

            {/* Filtered options */}
            <div className="p-2">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    type="button"
                    key={option}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      value === option ? "bg-primary-blue text-white" : "hover:bg-gray-100"
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
